import React, { useEffect, useState } from 'react';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import { generateAxiosInstance } from '../../hooks/useAuthorizedRequest';
import { generateAxiosInstanceSection } from '../../hooks/useAuthorizedRequestForSection';
import { generateAxiosInstanceInstitute } from '../../hooks/useAuthorizedRequestForInstitute';
import { useNavigate } from 'react-router-dom';
import JsonToExcelDownloader from '../../components/JsonToExcelDownloader';
import { GeneratePdf } from '../../components/GeneratePdf';
import { findPageNumber, searchArray, SortIcon } from '../../components/Navigation';
import Pagination from '../../components/Pagination';

const SectionExportStudentDetails = () => {
  const authorizedAxios = generateAxiosInstance()
  const authorizedAxiosSection = generateAxiosInstanceSection()
  const authorizedAxiosInstitute = generateAxiosInstanceInstitute()
  const instituteMasterId = localStorage.getItem("instituteMasterId")
  const sectionMasterId = localStorage.getItem("sectionMasterId")
  const initialFormState = {
    sectionMasterId: sectionMasterId,
    instituteMasterId: instituteMasterId,
    academicYearMasterId: '',
    instituteGradeMasterId: '',
    instituteDivisionMasterId: '',
  }

  const [gradeList, setGradeList] = useState([])
  const [divisionList, setDivisionList] = useState([])
  const [yearList, setYearList] = useState([])
  const [reportData, setReportData] = useState(initialFormState)

  const [studentReport, setStudentReport] = useState([])

  const [editId, setEditId] = useState(null)

  const [startIndex, setStartIndex] = useState(null);
  const [currPage, setCurrPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [displayedData, setDisplayedData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [saveId, setSaveId] = useState(null);

  const [nameSort, setNameSort] = useState(null);
  const [genderSort, setGenderSort] = useState(null);

  const [searchValue, setSearchValue] = useState(null);
  const searchKeys = ["studentName", "gender", "bloodGroup", "primaryContact", "secondaryContact", "email", "address"];

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const handleNavigate = (route) => {
    navigate(route);
  };

  useEffect(() => {
    getGradeList()
    getYearList()
  }, []);

  useEffect(() => {
    getStudentReport();
  }, [reportData]);

  useEffect(() => {
    if (searchValue === '' && searchValue !== null) {
      setCurrPage(1);
      getStudentReport();
    }
  }, [searchValue]);

  const handleEmptySearch = (e) => {
    setSearchValue(e.target.value);
  }

  const handleSearch = async () => {
    if (searchValue !== null) {
      setStudentReport(await searchArray("B", getApiUrl(), searchKeys, searchValue.trim()))
    }
  }

  useEffect(() => {
    if (saveId) {
      const pageNumber = findPageNumber(studentReport, saveId, itemsPerPage);
      setCurrPage(pageNumber);
    }
  }, [saveId])

  useEffect(() => {
    setTotalPages(Math.ceil(studentReport.length / itemsPerPage));
    const startIndex = (currPage - 1) * itemsPerPage;
    let endIndex = startIndex + itemsPerPage;

    if (endIndex > studentReport.length) {
      endIndex = studentReport.length;
    }
    setStartIndex(startIndex);
    setDisplayedData(studentReport.slice(startIndex, endIndex));
  }, [studentReport, currPage, itemsPerPage]);

  const getStudentReport = async () => {
    if (reportData.academicYearMasterId !== '') {
      try {
        const response = await authorizedAxiosSection.get(getApiUrl());
        setStudentReport(response.data.result);
      } catch (error) {
        setStudentReport([]);
        const errorMsg = error?.response?.data?.message || 'Something went wrong!';
        console.error('Error fetching student report:', errorMsg);
      }
    }
  }

  const getApiUrl = () => {
    let apiUrl = `/get-student-details-report?instituteMasterId=${instituteMasterId}&sectionMasterId=${sectionMasterId}&`;
    if (reportData.academicYearMasterId !== '') {
      apiUrl += `academicYearMasterId=${reportData.academicYearMasterId}&`;
    }
    if (reportData.instituteGradeMasterId !== '') {
      apiUrl += `instituteGradeMasterId=${reportData.instituteGradeMasterId}&`;
    }
    if (reportData.instituteDivisionMasterId !== '') {
      apiUrl += `instituteDivisionMasterId=${reportData.instituteDivisionMasterId}&`;
    }
    apiUrl = apiUrl.replace(/&$/, '');
    return apiUrl;
  }

  const getGradeList = () => {
    const apiUrlForSyllabus = `/get-institute-grade?instituteMasterId=${instituteMasterId}`;
    authorizedAxiosInstitute && authorizedAxiosInstitute.get(apiUrlForSyllabus)
      .then(response => {
        setGradeList(response.data.result)
      })
      .catch(error => {
        const errorMsg = error?.response?.data?.message || 'Something went wrong!';
        console.error('Error fetching data:', errorMsg);
      });
  }

  const getYearList = () => {
    const apiUrlForSyllabus = `/active-academic-years`;
    authorizedAxios && authorizedAxios.get(apiUrlForSyllabus)
      .then(response => {
        setYearList(response.data.result)
      })
      .catch(error => {
        const errorMsg = error?.response?.data?.message || 'Something went wrong!';
        console.error('Error fetching data:', errorMsg);
      });
  }

  const handleInputChange = (e) => {
    setReportData((prevData) => {
      return {
        ...prevData,
        [e.target.name]: e.target.value
      }
    })
  }

  const handleGradeChange = (e) => {
    setReportData((prevData) => {
      return {
        ...prevData,
        divisionId: '',
      }
    })
    setReportData((prevData) => {
      return {
        ...prevData,
        [e.target.name]: e.target.value
      }
    })
    const apiUrlForSyllabus = `/grade-wise-division?instituteMasterId=${instituteMasterId}&instituteGradeMasterId=${e.target.value}`;
    authorizedAxiosSection && authorizedAxiosSection.get(apiUrlForSyllabus)
      .then(response => {
        setDivisionList(response.data.result)
      })
      .catch(error => {
        const errorMsg = error?.response?.data?.message || 'Something went wrong!';
        console.error('Error fetching data:', errorMsg);
      });
  }

  const fileName = "student-report.xlsx";
  const dataArray = Object.values(studentReport || {}).map(student => ({
    "Name of Student": student?.studentName,
    Gender: student?.gender,
    "Blood Group": student?.bloodGroup,
    "Contact No": `${student?.primaryContact ?? ''}, ${student?.secondaryContact ?? ''}`,
    "E-mail": student?.email,
    Address: student?.address
  }));


  useEffect(() => {
    if (nameSort == true || nameSort == false) {
      setGenderSort(null);
    }
  }, [nameSort])

  useEffect(() => {
    if (genderSort == true || genderSort == false) {
      setNameSort(null);
    }
  }, [genderSort])

  return (
    <>
      <div className="wrapper d-flex flex-column min-vh-100 bg-light">
        <Header />
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb my-0 ms-2">
            <li className="breadcrumb-item dashboard" onClick={() => handleNavigate('/section-dashboard')}>
              <span>Dashboard</span>
            </li>
            <li className="breadcrumb-item active"><span>Export Student Details</span></li>
          </ol>
        </nav>
        <div className="container-lg">
          <div className="row">
            <div className="card border-info mx-auto mt-3">
              <div className="card-content collpase show">
                <div className="card-header" style={{ backgroundColor: '#3081D0', borderRadius: 0 }}>
                  <h4 className="text-white"><b><span className="fa fa-file-o"></span><i class="fa fa-file"></i>Export Student Details</b></h4>
                </div>
                <div className="card-body ">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="row">
                        <div className="col-md-6">
                          <label for="academicYearMasterId" className="col-form-label">Select Year :</label>
                          <select class="form-select" aria-label="Default select example" name='academicYearMasterId' onChange={handleInputChange} value={reportData.academicYearMasterId}>
                            <option selected value={''} disabled>Select Year</option>
                            {
                              yearList?.map((year) => {
                                return <option value={year.id}>{year.yearName}</option>
                              })
                            }
                          </select>
                        </div>

                        <div className="col-md-6">
                          <label for="instituteGradeMasterId" className="col-form-label">Select Class :</label>
                          <select class="form-select" aria-label="Default select example" name='instituteGradeMasterId' onChange={handleGradeChange} value={reportData.instituteGradeMasterId}>
                            <option selected value={''} disabled>Select Class</option>
                            {
                              gradeList?.map((grade) => {
                                return <option value={grade.id}>{grade.gradeName}</option>
                              })
                            }
                          </select>
                        </div>

                        <div className="col-md-6">
                          <label for="instituteDivisionMasterId" className="col-form-label">Select Division :</label>
                          <select class="form-select" aria-label="Default select example" name='instituteDivisionMasterId' onChange={handleInputChange} value={reportData.instituteDivisionMasterId}>
                            <option selected value={''} disabled>Select Division</option>
                            {
                              divisionList?.map((division) => {
                                return <option value={division.id}>{division.divisionName}</option>
                              })
                            }
                          </select>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-12 mt-3 mb-1" style={{ textAlign: 'center' }}>
                          <div className="col-auto">
                            <button className="btn btn-outline-danger delete_icon" type="button" style={{ marginRight: '20px' }} onClick={() => { setStudentReport([]); setEditId(''); setReportData(initialFormState) }}>Reset</button>
                          </div>
                        </div>

                        <div className="d-grid gap-2 mt-2">
                          <div class="tab-content rounded-bottom custom_table">
                            <div className='search_btn_row'>
                              <input
                                className="form-control mr-sm-2"
                                type="Search"
                                placeholder="Search"
                                value={searchValue}
                                onChange={handleEmptySearch}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter') {
                                    handleSearch();
                                  }
                                }}
                              />
                              <button className="btn btn-outline-primary" onClick={handleSearch}><i class="fa fa-search" aria-hidden="true"></i></button>
                            </div>

                            {studentReport && studentReport.length > 0 && <JsonToExcelDownloader dataArray={dataArray} fileName={fileName} />}
                            {studentReport && studentReport.length > 0 && <GeneratePdf id="tableContent" title={studentReport} headerTitle="Student Details" />}

                            <div id="tableContent">
                              <div class="tab-pane p-3 active preview" role="tabpanel" id="preview-1000">
                                <table class="table">
                                  <thead>
                                    <tr>
                                      <th scope="col">Sr. No.</th>
                                      <th scope="col">
                                        Name of Student
                                        <SortIcon sortState={nameSort} setSortState={setNameSort} setData={setStudentReport} setCurrPage={setCurrPage} sortingPath={"studentName"} />
                                      </th>
                                      <th scope="col">
                                        Gender
                                        <SortIcon sortState={genderSort} setSortState={setGenderSort} setData={setStudentReport} setCurrPage={setCurrPage} sortingPath={"gender"} />
                                      </th>
                                      <th scope="col">Blood Group</th>
                                      <th scope="col">Contact No</th>
                                      <th scope="col">E-mail</th>
                                      <th scope="col">Address</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {
                                      displayedData.length > 0 ?
                                        displayedData.map((student, index) => {
                                          return (
                                            <>
                                              <tr>
                                                <th scope="row">{startIndex + index + 1}</th>
                                                <td>{student.studentName}</td>
                                                <td>{student.gender}</td>
                                                <td>{student?.bloodGroup}</td>
                                                <td>{student?.primaryContact}, <br/>{student?.secondaryContact}</td>
                                                <td>{student?.email}</td>
                                                <td>{student?.address}</td>
                                              </tr>
                                            </>
                                          )
                                        })
                                        :
                                        (<tr>
                                          <th colspan={"100%"} style={{ textAlign: 'center' }}>No Student Available</th>
                                        </tr>)
                                    }
                                  </tbody>
                                </table>
                                <Pagination currPage={currPage} setCurrPage={setCurrPage} totalPages={totalPages} itemsPerPage={itemsPerPage} setItemsPerPage={setItemsPerPage} totalItem={studentReport.length} />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  )
};

export default SectionExportStudentDetails;